import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { FileType } from '../../../../../types/common.types';
import { HashTag, Member, Poll, Post } from '../../../../../types/entity.types';

import {
  containsImageOrVideo,
  extractWordAtCaretPosition,
  generateUUID,
  getClassNames,
  isHashtag,
  isMention,
  replaceWordAtCaretPosition
} from '../../../../../utils/util';

import Button from '../../../../common/button/Button';
import FilePicker from '../../../../common/input/file-picker/FilePicker';
import TribePollModal from '../tribe-poll-modal/TribePollModal';
import TribePoll from '../tribe-poll/TribePoll';
import TribeThumbnails from '../tribe-thumbnails/TribeThumbnails';

import TribeFilesContainer from '../../../../../containers/programme/tribe/tribe-files/TribeFiles.container';

import { ReactComponent as PaperclipIcon } from '../../../../../resources/icons/paperclip.svg';
import { ReactComponent as PollIcon } from '../../../../../resources/icons/poll.svg';

import './tribe-input.scss';

type Props = {
  post?: Post;
  placeholder: string;
  files?: FileType[];
  hashtags?: HashTag[];
  members?: Member[];
  comment?: boolean;
  addAttachment: (file: File | undefined) => void;
  removeAttachment: (file: File | FileType) => void;
  createPost: (value: string) => Promise<void>;
  updatePost: (value: string) => Promise<void>;
  createComment: (message: string, callbackFn: () => void) => Promise<void>;
  createPoll: (message: string, poll: Poll) => Promise<void>;
  updatePoll: (message: string, poll: Poll) => Promise<void>;
  focus?: boolean;
};

const TribeInput = ({
  post,
  placeholder,
  files = [],
  hashtags = [],
  members = [],
  comment = false,
  addAttachment,
  removeAttachment,
  createPost,
  updatePost,
  createComment,
  createPoll,
  updatePoll,
  focus = false
}: Props) => {
  const [message, setMessage] = useState('');
  const [caretPosition, setCaretPosition] = useState<number | undefined>(undefined);
  const [suggestions, setSuggestions] = useState<HashTag[] | Member[]>([]);
  const [showPollModal, setShowPollModal] = useState(false);
  const [poll, setPoll] = useState<Poll | undefined>(undefined);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (focus) {
      textareaRef.current?.focus();
    }
  }, [focus]);

  useEffect(() => {
    if (post) {
      setMessage(post.text ?? '');

      const { question, options } = post;

      if (question && options) {
        const poll: Poll = {
          question,
          options: options.map((option) => {
            return { id: generateUUID(), value: option, votes: 0 };
          }),
          allowMultipleSelection: post.allowMultipleSelection,
          anonymous: post.anonymous
        };

        setPoll(poll);
      }
    }
  }, [post]);

  const disablePostButton = useMemo(() => {
    const messageIsEmpty = message.trim().length === 0;
    const noFilesAttached = files.length === 0;
    const noPoll = !poll;

    return messageIsEmpty && noFilesAttached && noPoll;
  }, [message, files, poll]);

  const filterSuggestions = (word: string) => {
    if (isHashtag(word)) {
      setSuggestions(
        hashtags.filter((hashtagInList) => {
          if (!hashtagInList || !hashtagInList.name) {
            return false;
          }

          return hashtagInList.name.toLowerCase().includes(word.toLowerCase());
        })
      );
    } else if (isMention(word)) {
      setSuggestions(
        members.filter(
          (member) =>
            member.username!.toLowerCase().includes(word.substring(1).toLowerCase()) &&
            !member.username!.includes('Deleted Account')
        )
      );
    } else {
      setSuggestions([]);
    }
  };

  const handleTextareaClick = ({ target }: React.MouseEvent<HTMLTextAreaElement>) => {
    const { textContent } = target as HTMLTextAreaElement;
    const caretPosition = textareaRef.current?.selectionEnd;
    const [word] = extractWordAtCaretPosition(textContent, caretPosition);

    filterSuggestions(word);
    setCaretPosition(caretPosition);
  };

  const handleTextareaKeyUp = ({ keyCode, target }: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (keyCode < 41 && keyCode > 36) {
      const { value } = target as HTMLTextAreaElement;
      const caretPosition = textareaRef.current?.selectionEnd;
      const [word] = extractWordAtCaretPosition(value, caretPosition);

      filterSuggestions(word);
      setCaretPosition(caretPosition);
    }
  };

  const handleMessageChange = ({ target }: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = target;
    const caretPosition = textareaRef.current?.selectionEnd;
    const [word] = extractWordAtCaretPosition(value, caretPosition);

    filterSuggestions(word);
    setCaretPosition(caretPosition);
    setMessage(value);
  };

  const handleSuggestionClick = (suggestion: string) => {
    const isHashtagSuggestion = suggestion.includes('#');

    let newMessage: string;
    if (isHashtagSuggestion) {
      newMessage = replaceWordAtCaretPosition(message, suggestion, caretPosition);
    } else {
      newMessage = replaceWordAtCaretPosition(message, `@${suggestion}`, caretPosition);
    }

    setMessage(newMessage);
    textareaRef.current?.focus();
    setSuggestions([]);
  };

  const handlePostClick = async () => {
    if (comment) {
      await createComment(message, () => setMessage(''));
    } else if (post && poll) {
      await updatePoll(message, poll);
    } else if (post) {
      await updatePost(message);
    } else if (poll) {
      await createPoll(message, poll);
    } else {
      await createPost(message);
    }
  };

  const hasFiles = files.length > 0;

  return (
    <div className={getClassNames('tribe-input', { comment })} data-testid="tribe-input">
      <textarea
        value={message}
        onClick={handleTextareaClick}
        onChange={handleMessageChange}
        onKeyUp={handleTextareaKeyUp}
        placeholder={placeholder}
        ref={textareaRef}
      />
      {suggestions.length > 0 && (
        <div
          className={getClassNames('tribe-input__suggestions', {
            comment,
            border: hasFiles,
            margin: hasFiles && !containsImageOrVideo(files)
          })}
        >
          {suggestions.map((suggestion) => {
            const value = ((suggestion as HashTag).name || (suggestion as Member).username) as string;
            return (
              <p
                className={getClassNames('tribe-input__suggestion', { comment })}
                onClick={() => handleSuggestionClick(value)}
                key={value}
              >
                {value}
              </p>
            );
          })}
        </div>
      )}
      <TribeThumbnails files={files} deleteFile={removeAttachment} comment={comment} />
      <TribeFilesContainer files={files} deleteFile={removeAttachment} comment={comment} />
      {poll && (
        <TribePoll poll={poll} onEditClick={() => setShowPollModal(true)} onDeleteClick={() => setPoll(undefined)} />
      )}
      <div className={getClassNames('tribe-input__actions', { comment })}>
        <div className="tribe-input__actions__icon-container">
          <FilePicker
            triggerElement={
              <PaperclipIcon
                className="tribe-input__paperclip-icon"
                onClick={() => fileRef.current?.click()}
                data-testid="tribe-input-attachment-icon"
              />
            }
            fileRef={fileRef}
            onChange={({ target }) => addAttachment(target.files ? target.files[0] : undefined)}
          />
          {!(comment || (post && !poll)) && (
            <PollIcon
              className="tribe-input__poll-icon"
              data-testid="tribe-input-poll-icon"
              onClick={() => setShowPollModal(true)}
            />
          )}
        </div>
        <Button text={post ? 'SAVE' : 'POST'} onClick={handlePostClick} disabled={disablePostButton} />
      </div>

      <TribePollModal show={showPollModal} setShow={setShowPollModal} poll={poll} setPoll={setPoll} />
    </div>
  );
};

export default TribeInput;
